import React, { Fragment, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay';
import { getBuyerId } from 'dibs-cookie-jar';
import { addLoginHandlers } from 'dibs-buyer-layout/src/utils/loginHandlers';
import get from 'lodash.get';
import tracking from 'dibs-tracking';

// TODO Shanie: FINDING-14024 - Remove "Old" components post A/B Test
import { HpRespInformationalContentRelayContainer } from './HpRespInformationalContent/HpRespInformationalContent';
import { HpRespHeroModuleOld } from './HpRespHeroModuleOld/HpRespHeroModuleOld';
import { HpRespRecentlyViewedItemsLoader } from './HpRespRecentlyViewedItems/HpRespRecentlyViewedItemsLoader';
import { HpRespLocationsModule } from './HpRespLocationsModule/HpRespLocationsModule';
import { HpRespPersonalizedCategoriesLoader } from './HpRespPersonalizedCategories/HpRespPersonalizedCategoriesLoader';
import { HpRespPromoModulePrimaryOld } from './HpRespPromoModuleOld/HpRespPromoModulePrimaryOld';
import { HpRespCollectionsModuleOld } from './HpRespCollectionsModuleOld/HpRespCollectionsModuleOld';
import { HpRespFeaturedModule } from './HpRespFeaturedModule/HpRespFeaturedModule';
import { HpRespPromoModuleSecondaryOld } from './HpRespPromoModuleOld/HpRespPromoModuleSecondaryOld';
import { HpRespEditorialModuleOld } from './HpRespEditorialModuleOld/HpRespEditorialModuleOld';
import { HpRespShopByModule } from './HpRespShopByModule/HpRespShopByModule';
import { HomePageContext } from './HomePageContext';
import { connectContext } from '../context/helpers/connectContext';
import { getItemsPerPage } from './helpers/getItemsPerPage';

const NUM_MOBILE_LOCATIONS = 1;
const NUM_RESP_LOCATIONS = 3;

const HpRespLayoutComponent = props => {
    const [userId, setUserId] = useState(null);
    const [isClient, setIsClient] = useState(null);

    const { relay, isLoggedInUser, viewer, isMobile, isTablet } = props;
    const user = get(viewer, 'user') || null;

    const doRefetch = useCallback(
        (additionalVariables = {}) => {
            relay.refetch(
                {
                    isClient: true,
                    hasUserId: !!userId,
                    userId: userId || '',
                    userIds: userId ? [userId] : [],
                    ...additionalVariables,
                },
                null,
                () => setIsClient(true)
            );
        },
        [userId, relay]
    );

    const setUserData = useCallback(() => {
        const cookieUserId = getBuyerId(document.cookie);

        if (cookieUserId && !userId) {
            setUserId(cookieUserId);
        }
    }, [userId]);

    useEffect(() => {
        if (!isLoggedInUser && !isClient) {
            doRefetch();
        }
    }, [doRefetch, isLoggedInUser, isClient]);

    useEffect(() => {
        if (userId && !user) {
            doRefetch();
        }
    }, [doRefetch, userId, user]);

    useEffect(() => {
        tracking.trackPageview();
        setUserData();
        addLoginHandlers(data => {
            if (data.user && data.user.isVerifiedTrade) {
                // keep this to make sure a trade member
                // logging in on the hp is "redirected"
                // to the trade homepage
                window.location.reload(true);
                return;
            }
            setUserData();
        });
    }, [setUserData]);

    const { homepage = {}, showNewHomepageLocationsModule } = viewer;

    const itemsPerPage = getItemsPerPage({ isMobile, isTablet });
    const { personalization = null } = homepage;
    const showLoggedInModules = isLoggedInUser || userId;
    const showPersonalizedCategoriesModule = isClient && showLoggedInModules;
    const pageSetOffset = !isMobile;

    return (
        <Fragment>
            <HpRespInformationalContentRelayContainer viewer={viewer} />
            <HpRespHeroModuleOld homepage={homepage} />
            {isClient && (
                <HpRespRecentlyViewedItemsLoader
                    itemEdges={get(viewer, 'items') || null}
                    user={get(viewer, 'user') || null}
                    fetchItemTiles={doRefetch}
                    itemsPerPage={itemsPerPage}
                    showBarHeader
                    additionalSpacing={isMobile}
                />
            )}
            {showPersonalizedCategoriesModule && (
                <HpRespPersonalizedCategoriesLoader
                    personalization={personalization}
                    viewer={viewer}
                    hasBorder={!isMobile}
                    hasNoPageGutter={isMobile}
                    pageSetOffset={pageSetOffset}
                />
            )}
            <HpRespPromoModulePrimaryOld homepage={homepage} />
            <HpRespCollectionsModuleOld homepage={homepage} />
            <HpRespFeaturedModule homepage={homepage} />
            <HpRespPromoModuleSecondaryOld homepage={homepage} />
            <HpRespEditorialModuleOld homepage={homepage} />
            {showNewHomepageLocationsModule && (
                <HpRespLocationsModule
                    homepage={homepage}
                    viewer={viewer || null}
                    additionalTopSpacing={isMobile}
                    numOfVisibleLocations={isMobile ? NUM_MOBILE_LOCATIONS : NUM_RESP_LOCATIONS}
                    pageSetOffset={pageSetOffset}
                    showLocationLink={isMobile}
                />
            )}
            <HpRespShopByModule homepage={homepage} />
        </Fragment>
    );
};

HpRespLayoutComponent.propTypes = {
    isLoggedInUser: PropTypes.bool,
    isMobile: PropTypes.bool,
    isTablet: PropTypes.bool,
    relay: PropTypes.object.isRequired,
    viewer: PropTypes.object.isRequired,
};

function mapContextToProps({ isLoggedInUser, isMobile, isTablet }) {
    return {
        isMobile,
        isTablet,
        isLoggedInUser,
    };
}

const HpRespLayoutWithContext = connectContext(HomePageContext, mapContextToProps)(
    HpRespLayoutComponent
);

export const HpRespLayout = createRefetchContainer(
    HpRespLayoutWithContext,
    {
        viewer: graphql`
            fragment HpRespLayout_viewer on Viewer {
                showNewHomepageLocationsModule: isEnabled(key: "showNewHomepageLocationsModule")
                ...HpRespPersonalizedCategories_viewer
                homepage(rootId: $homepageId) {
                    ...HpRespHeroModuleOld_homepage
                    ...HpRespPromoModulePrimaryOld_homepage
                    ...HpRespCollectionsModuleOld_homepage
                    ...HpRespFeaturedModule_homepage
                    ...HpRespPromoModuleSecondaryOld_homepage
                    ...HpRespEditorialModuleOld_homepage
                    ...HpRespShopByModule_homepage
                    ...HpRespLocationsModule_homepage
                    personalization(userId: $userId, first: 3, numItems: 3)
                        @include(if: $hasUserId) {
                        ...HpRespPersonalizedCategories_personalization
                    }
                }
                ...HpRespLocationsModule_viewer
                ...HpRespInformationalContent_viewer
                user(userId: $userId) @include(if: $hasUserId) {
                    ...HpRespRecentlyViewedItems_user
                }
                items(itemIds: $recentlyViewedItemIds) @include(if: $hasRecentlyViewedItems) {
                    ...HpRespRecentlyViewedItems_itemEdges
                }
            }
        `,
    },
    graphql`
        query HpRespLayoutRefetchQuery(
            $hasRecentlyViewedItems: Boolean!
            $hasUserId: Boolean!
            $userId: String!
            $homepageId: String!
            $recentlyViewedItemIds: [String]
            $isClient: Boolean!
        ) {
            viewer {
                homepage(rootId: $homepageId) {
                    personalization(userId: $userId, first: 3, numItems: 3)
                        @include(if: $hasUserId) {
                        ...HpRespPersonalizedCategories_personalization
                    }
                }
                ...HpRespLocationsModule_viewer
                user(userId: $userId) @include(if: $hasUserId) {
                    ...HpRespRecentlyViewedItems_user
                }
                items(itemIds: $recentlyViewedItemIds) @include(if: $hasRecentlyViewedItems) {
                    ...HpRespRecentlyViewedItems_itemEdges
                }
            }
        }
    `
);
